import api from "../api/axios";

export const MAX_REPLAY_RANGE_MS = 7 * 24 * 60 * 60 * 1000;
export const REPLAY_PAGE_SIZE = 1000;

export class GpsReplaySnapshotError extends Error {
  constructor(code) {
    super("GPS replay snapshot could not be loaded completely.");
    this.name = "GpsReplaySnapshotError";
    this.code = code;
  }
}

function validDate(value) {
  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function finiteOrNull(value) {
  return value === null || value === undefined ? null : Number(value);
}

export function normalizeReplayObservation(value, ordinal) {
  const recordedAt = validDate(value?.recordedAt);
  const receivedAt = validDate(value?.receivedAt);
  const latitude = Number(value?.coordinates?.latitude);
  const longitude = Number(value?.coordinates?.longitude);
  const speedKph = finiteOrNull(value?.speedKph);
  const headingDegrees = finiteOrNull(value?.headingDegrees);
  const accuracyMeters = finiteOrNull(value?.accuracyMeters);
  const satelliteCount = finiteOrNull(value?.satelliteCount);
  if (
    value?.source !== "physical" || !recordedAt || !receivedAt ||
    !Number.isFinite(latitude) || latitude < -90 || latitude > 90 ||
    !Number.isFinite(longitude) || longitude < -180 || longitude > 180 ||
    (latitude === 0 && longitude === 0) || speedKph === null || speedKph < 0 ||
    headingDegrees === null || headingDegrees < 0 || headingDegrees >= 360 ||
    (accuracyMeters !== null && (!Number.isFinite(accuracyMeters) || accuracyMeters < 0)) ||
    (satelliteCount !== null && (!Number.isSafeInteger(satelliteCount) || satelliteCount < 0)) ||
    ![true, false, null, undefined].includes(value?.ignition)
  ) {
    throw new GpsReplaySnapshotError("GPS_REPLAY_POSITION_INVALID");
  }
  return Object.freeze({
    ordinal,
    coordinates: Object.freeze({ latitude, longitude }),
    speedKph,
    headingDegrees,
    ignition: typeof value.ignition === "boolean" ? value.ignition : null,
    accuracyMeters,
    satelliteCount,
    alarms: Object.freeze(Array.isArray(value.alarms) ? [...value.alarms] : []),
    outOfOrder: value.outOfOrder === true,
    recordedAt,
    receivedAt,
    source: "physical",
  });
}

export async function loadGpsReplaySnapshot({
  vehicleId,
  from,
  to,
  signal,
  client = api,
  pageSize = REPLAY_PAGE_SIZE,
} = {}) {
  const rangeFrom = validDate(from);
  const rangeTo = validDate(to);
  if (
    typeof vehicleId !== "string" || !vehicleId || !rangeFrom || !rangeTo ||
    rangeFrom >= rangeTo || rangeTo - rangeFrom > MAX_REPLAY_RANGE_MS ||
    !Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > REPLAY_PAGE_SIZE
  ) {
    throw new GpsReplaySnapshotError("GPS_REPLAY_RANGE_INVALID");
  }

  const observations = [];
  const seenCursors = new Set();
  let cursor = null;
  let pageCount = 0;
  do {
    const params = {
      from: rangeFrom.toISOString(),
      to: rangeTo.toISOString(),
      limit: pageSize,
      source: "physical",
      ...(cursor ? { cursor } : {}),
    };
    const response = await client.get(`/gps/vehicles/${vehicleId}/history`, { params, signal });
    const body = response?.data;
    if (!body || !Array.isArray(body.positions) ||
        (body.source !== undefined && body.source !== "physical")) {
      throw new GpsReplaySnapshotError("GPS_REPLAY_PAGE_INVALID");
    }
    for (const position of body.positions) {
      observations.push(normalizeReplayObservation(position, observations.length));
    }
    pageCount += 1;
    if (body.hasMore !== true) {
      if (body.nextCursor) throw new GpsReplaySnapshotError("GPS_REPLAY_PAGE_INVALID");
      cursor = null;
      break;
    }
    if (typeof body.nextCursor !== "string" || !body.nextCursor || seenCursors.has(body.nextCursor)) {
      throw new GpsReplaySnapshotError("GPS_REPLAY_CURSOR_INVALID");
    }
    seenCursors.add(body.nextCursor);
    cursor = body.nextCursor;
  } while (cursor);

  observations.sort((left, right) =>
    left.recordedAt.getTime() - right.recordedAt.getTime() || left.ordinal - right.ordinal
  );
  return Object.freeze({
    source: "physical",
    range: Object.freeze({ from: rangeFrom, to: rangeTo }),
    observations: Object.freeze(observations),
    pageCount,
  });
}
